/**
 * NavigationMenu - Core Component (Passthrough Wrapper)
 *
 * This is a thin wrapper around shadcn/ui NavigationMenu components.
 * It provides a consistent import path and allows for future project-specific
 * defaults or modifications without changing consumer code.
 *
 * Purpose:
 * - Consistent import path: always import from '@/app/_components/core'
 * - Type re-exports for convenience
 * - Default viewport=false (viewport rendering disabled by default)
 * - Future-proof: modifications can be added here without breaking changes
 *
 * This component does NOT add custom styling - it passes through
 * all props directly to the underlying shadcn/ui components.
 *
 * @see {@link file://./../ui/navigation-menu.tsx} for the underlying implementation
 */
import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import {
  NavigationMenu as UiNavigationMenu,
  NavigationMenuContent as UiNavigationMenuContent,
  NavigationMenuItem as UiNavigationMenuItem,
  NavigationMenuLink as UiNavigationMenuLink,
  NavigationMenuList as UiNavigationMenuList,
  NavigationMenuTrigger as UiNavigationMenuTrigger,
  NavigationMenuIndicator as UiNavigationMenuIndicator,
  NavigationMenuViewport as UiNavigationMenuViewport,
  navigationMenuTriggerStyle as uiNavigationMenuTriggerStyle,
} from '@/app/_components/ui/navigation-menu';
import { cn } from '@/app/_lib/utils';

/**
 * NavigationMenu - Core wrapper component
 * Thin wrapper that passes through props and classNames to ui/navigation-menu
 * Default: viewport = false
 */
export const NavigationMenu = forwardRef<
  HTMLElement,
  ComponentPropsWithoutRef<typeof UiNavigationMenu>
>(({ className, viewport = false, children, ...props }, ref) => (
  <UiNavigationMenu
    ref={ref}
    viewport={viewport}
    className={cn(className)}
    {...props}
  >
    {children}
  </UiNavigationMenu>
));

NavigationMenu.displayName = 'NavigationMenu';

/**
 * NavigationMenuList - Core wrapper component
 * Thin wrapper that passes through props and classNames
 */
export const NavigationMenuList = forwardRef<
  HTMLUListElement,
  ComponentPropsWithoutRef<typeof UiNavigationMenuList>
>(({ className, ...props }, ref) => (
  <UiNavigationMenuList
    ref={ref}
    className={cn(className)}
    {...props}
  />
));

NavigationMenuList.displayName = 'NavigationMenuList';

/**
 * NavigationMenuItem - Core wrapper component
 * Thin wrapper that passes through props and classNames
 */
export const NavigationMenuItem = forwardRef<
  HTMLLIElement,
  ComponentPropsWithoutRef<typeof UiNavigationMenuItem>
>(({ className, ...props }, ref) => (
  <UiNavigationMenuItem
    ref={ref}
    className={cn(className)}
    {...props}
  />
));

NavigationMenuItem.displayName = 'NavigationMenuItem';

/**
 * NavigationMenuTrigger - Core wrapper component
 * Thin wrapper that passes through props and classNames
 */
export const NavigationMenuTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof UiNavigationMenuTrigger>
>(({ className, ...props }, ref) => (
  <UiNavigationMenuTrigger
    ref={ref}
    className={cn(className)}
    {...props}
  />
));

NavigationMenuTrigger.displayName = 'NavigationMenuTrigger';

/**
 * NavigationMenuContent - Core wrapper component
 * Thin wrapper that passes through props and classNames
 */
export const NavigationMenuContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof UiNavigationMenuContent>
>(({ className, ...props }, ref) => (
  <UiNavigationMenuContent
    ref={ref}
    className={cn(className)}
    {...props}
  />
));

NavigationMenuContent.displayName = 'NavigationMenuContent';

/**
 * NavigationMenuLink - Core wrapper component
 * Thin wrapper that passes through props and classNames
 */
export const NavigationMenuLink = forwardRef<
  HTMLAnchorElement,
  ComponentPropsWithoutRef<typeof UiNavigationMenuLink>
>(({ className, ...props }, ref) => (
  <UiNavigationMenuLink
    ref={ref}
    className={cn(className)}
    {...props}
  />
));

NavigationMenuLink.displayName = 'NavigationMenuLink';

/**
 * NavigationMenuViewport - Core wrapper component
 * Thin wrapper that passes through props and classNames
 */
export const NavigationMenuViewport = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof UiNavigationMenuViewport>
>(({ className, ...props }, ref) => (
  <UiNavigationMenuViewport
    ref={ref}
    className={cn(className)}
    {...props}
  />
));

NavigationMenuViewport.displayName = 'NavigationMenuViewport';

/**
 * NavigationMenuIndicator - Core wrapper component
 * Thin wrapper that passes through props and classNames
 */
export const NavigationMenuIndicator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof UiNavigationMenuIndicator>
>(({ className, ...props }, ref) => (
  <UiNavigationMenuIndicator
    ref={ref}
    className={cn(className)}
    {...props}
  />
));

NavigationMenuIndicator.displayName = 'NavigationMenuIndicator';

/**
 * navigationMenuTriggerStyle - Re-export from ui
 */
export const navigationMenuTriggerStyle = uiNavigationMenuTriggerStyle;

/**
 * Type exports for convenience
 */
export type NavigationMenuProps = ComponentPropsWithoutRef<typeof NavigationMenu>;

export default NavigationMenu;
